import { useState, type FormEvent } from 'react';
import { ApiError, downloadMonthlyReport } from './api';
import { ManagerNavigation } from './ManagerNavigation';

function currentTaipeiMonth(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit'
  }).format(new Date());
}

export function MonthlyReport(): React.JSX.Element {
  const [month, setMonth] = useState(currentTaipeiMonth);
  const [managerPassword, setManagerPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const download = async (reportType: 'sales' | 'merchandising') => {
    if (!month || !managerPassword || busy) return;
    setBusy(true);
    setMessage('正在讀取所選月份並產生 Excel…');
    try {
      const file = await downloadMonthlyReport(month, managerPassword, reportType);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setMessage(`已產生 ${file.filename}，請查看下載項目。`);
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : '月報產生失敗，請稍後再試。');
    } finally {
      setBusy(false);
    }
  };
  const submit = (event: FormEvent) => { event.preventDefault(); void download('sales'); };

  return (
    <>
      <ManagerNavigation pageTitle="銷售月報" currentPage="sales-monthly" />
      <main className="monthly-report-shell">
        <section className="monthly-report-card" aria-labelledby="monthly-report-title">
        <p className="eyebrow">MNT STORE SALES</p>
        <h1 id="monthly-report-title">下載駐點銷售月報</h1>
        <p className="monthly-report-intro">
          每次只匯出所選月份。Excel 內含完整「MNT總覽」與「MNT明細」，銷售資料已自動填入，不用再複製 Google Sheet。
        </p>
        <form onSubmit={submit} className="monthly-report-form">
          <label htmlFor="report-month">報表月份</label>
          <input
            id="report-month"
            type="month"
            required
            value={month}
            max={currentTaipeiMonth()}
            onChange={event => setMonth(event.target.value)}
          />
          <label htmlFor="report-manager-password">管理密碼</label>
          <input
            id="report-manager-password"
            type="password"
            required
            autoComplete="current-password"
            value={managerPassword}
            onChange={event => setManagerPassword(event.target.value)}
          />
          <button type="submit" disabled={busy || !month || !managerPassword}>
            {busy ? '產生中…' : '產生並下載 Excel'}
          </button>
          <button type="button" disabled={busy || !month || !managerPassword} onClick={() => { void download('merchandising'); }}>
            下載商化樞紐資料
          </button>
        </form>
        <p className="monthly-report-intro">商化樞紐資料會讀取 AI 紀錄與當月任務，下載同一個月份的「MNT」資料及「MNT訪店彙整」空白版面，供管理人員手動建立樞紐分析表。</p>
        <p className="monthly-report-status" role="status" aria-live="polite">{message}</p>
        <ul className="monthly-report-notes">
          <li>總覽的機型排行依當月成交品項與數量動態產生。</li>
          <li>GPS地圖可直接點開 Google 地圖；明顯超出門市範圍會紅色標示，精準度不足會橘色標示。</li>
          <li>駐點班表保留空白供管理者填入原排班；LINE 到點／下班會自動計算各店實際總銷。</li>
          <li>銷售比例只適用混合品牌店點；三星專賣店標示「-」。</li>
          <li>當月尚未結束時，檔內會標示資料截止時間與「未結月」。</li>
        </ul>
        </section>
      </main>
    </>
  );
}
